<script lang="ts" setup>
import { ref, Ref, computed, WritableComputedRef, PropType, watch } from 'vue';
import {
    ConnectFlowModelingActions,
    ConnectFlowModelingGetters,
    ConnectFlowModelingMutations,
    ConnectFlowAudioModel,
    VueFlowNodeModel,
} from '@/types';
import store from '@/store';
import { QuestionMarkCircleIcon } from '@heroicons/vue/outline';
import { Handle, Position } from '@vue-flow/core';
import { useVuelidate, Validation, ValidationArgs } from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import UiTextInput from '@/components/UiTextInput.vue';

const { t } = useI18n();

const props = defineProps({
    node: {
        type: Object as PropType<VueFlowNodeModel>,
        required: true,
    },
});

const showForm = ref(false);

const nodeWritable: WritableComputedRef<VueFlowNodeModel> = computed({
    get: () => store.getters['flow/' + ConnectFlowModelingGetters.ELEMENT_BY_ID](props.node.id),
    set: (value: VueFlowNodeModel) => {
        store.commit('flow/' + ConnectFlowModelingMutations.UPDATE_ELEMENT, {
            idx: null,
            value,
        });
    },
});

const audioOptions = computed(
    () => store.getters['flow/' + ConnectFlowModelingGetters.AUDIO_OPTIONS],
);
interface IClientInfoFormState {
    audio: null | ConnectFlowAudioModel;
    text: undefined | string;
    timeout: number;
}

const formState: Ref<IClientInfoFormState> = ref({
    audio: null,
    text: undefined,
    timeout: 10,
});

const textValidation = t('connectFlow.node.clientInfo.validation');
const atLeastOneFilled = helpers.withMessage(textValidation, (value, state) => {
    return !!state.audio || !!state.text;
});
const rules = computed<ValidationArgs<IClientInfoFormState>>(() => {
    const r: ValidationArgs<IClientInfoFormState> = {
        audio: {
            atLeastOneFilled,
        },
        text: {
            atLeastOneFilled,
        },
        timeout: {},
    };
    return r;
});
const v$: Ref<Validation<ValidationArgs<IClientInfoFormState>>> = useVuelidate(rules, formState);

const nodeText = computed(() => {
    let text = t('connectFlow.node.clientInfo.empty');
    if (nodeWritable.value.data.related) {
        text = nodeWritable.value.data.related.label;
    } else if (nodeWritable.value.data.textToAudio) {
        text = t('connectFlow.node.clientInfo.textToAudio');
    }
    return text;
});

const isFormCorrect = ref(true);

function removeNode() {
    store.dispatch('flow/' + ConnectFlowModelingActions.DELETE_NODE, {
        nodeId: nodeWritable.value.id,
        parentId: nodeWritable.value.parentId,
    });
}
async function onSubmit() {
    isFormCorrect.value = await v$.value.$validate();
    if (isFormCorrect.value) {
        if (formState.value.audio) {
            nodeWritable.value.data.related = {
                value: formState.value.audio.id,
                label: formState.value.audio.name,
            };
            nodeWritable.value.data.textToAudio = undefined;
        } else if (formState.value.text) {
            nodeWritable.value.data.textToAudio = formState.value.text;
            nodeWritable.value.data.related = null;
        }
        showForm.value = false;
    }
    nodeWritable.value.data.hasErrors = !isFormCorrect.value;
    v$.value.$reset();
}
watch([nodeWritable.value], () => {
    formState.value.audio = audioOptions.value.find(
        (audio: ConnectFlowAudioModel) => audio.id == nodeWritable.value.data.related?.value,
    );
    formState.value.text = nodeWritable.value.data.textToAudio;

    v$.value.$validate().then((result) => {
        isFormCorrect.value = result;
        nodeWritable.value.data.hasErrors = !isFormCorrect.value;
    });
});
</script>

<template>
    <UiNode
        :nodeId="node.id"
        :title="$t('connectFlow.node.clientInfo.title')"
        :icon="QuestionMarkCircleIcon"
        showActions
        @editNode="showForm = true"
        @removeNode="removeNode"
        :class="isFormCorrect ? '' : 'border-red-300 shadow-red-300'"
    >
        <div class="font-medium text-gray-600">
            <span>
                {{ nodeText }}
            </span>
        </div>

        <Handle
            id="timeout"
            type="target"
            :position="Position.Left"
            :connectable="false"
            style="top: 45px"
        />

        <Handle
            id="timeout"
            type="source"
            :connectable="false"
            :position="Position.Left"
            style="top: 35px"
        />

        <Handle
            id="invalid"
            type="target"
            :connectable="false"
            :position="Position.Left"
            style="top: auto; bottom: 25px"
        />

        <Handle
            id="invalid"
            type="source"
            :position="Position.Left"
            :connectable="false"
            style="top: auto; bottom: 35px"
        />
    </UiNode>
    <UiRightPanel
        v-model="showForm"
        :title="$t('connectFlow.node.clientInfo.title')"
        :icon="QuestionMarkCircleIcon"
    >
        <form autocomplete="off" class="flex flex-col gap-3" @submit.prevent="onSubmit">
            <div class="sm:col-span-12">
                <label class="mb-2 block text-sm font-medium text-gray-800">
                    {{ $t('connectFlow.node.clientInfo.select') }}
                </label>
                <UiMultiselect
                    name="audio"
                    v-model="v$.audio.$model"
                    :options="audioOptions"
                    :errors="v$.audio.$silentErrors"
                    :disabled="formState.text?.length > 0"
                    label="name"
                    value-prop="id"
                    can-clear
                />
            </div>
            <UiTextArea
                :label="$t('connectFlow.node.clientInfo.write')"
                name="text"
                v-model="v$.text.$model"
                :disabled="formState.audio?.name.length > 0"
                :errors="v$.text.$silentErrors"
            />

            <UiTextInput
                :label="$t('connectFlow.node.clientInfo.timeout')"
                name="timeout"
                v-model="v$.timeout.$model"
            />
            <div class="mt-6 grid grid-cols-2 place-content-around gap-4 text-center">
                <UiButton variant="white" text-variant="gray-700" @click="showForm = false">
                    {{ $t('core.actions.Cancel') }}
                </UiButton>
                <UiButton type="submit" variant="primary" text-variant="white">
                    {{ $t('core.actions.Apply') }}
                </UiButton>
            </div>
        </form>
    </UiRightPanel>
</template>
